<template>

<div class="consent">

	<h1 class="gg-title">Continue to {{ client.name }}</h1>
	<p class="gg-sub">{{ sub }}</p>

	<!-- who you are -->
	<div class="consent-who">
		<img v-if="profile.avatar && !avatarFailed" class="consent-avatar" :src="profile.avatar" alt="" v-on:error="avatarFailed = true" />
		<div v-else class="consent-avatar consent-avatar--fallback">{{ initial }}</div>
		<div class="consent-who-text">
			<div class="consent-handle">@{{ profile.username }}</div>
			<div class="consent-name" v-if="profile.name">{{ profile.name }}</div>
		</div>
	</div>

	<!-- what it gets -->
	<div class="consent-scopes" v-if="shown.length">
		<div class="consent-scopes-head">This will share with {{ client.name }} your public data only:</div>
		<div class="consent-scope" v-for="s in shown" :key="s.key">
			<span class="consent-tick">✓</span>
			<span>{{ s.label }}</span>
		</div>
	</div>

	<p class="gg-error-text" v-if="error">{{ error }}</p>

	<button class="gg-btn gg-btn--primary consent-go" :disabled="busy" v-on:click="$emit('allow')">
		<span v-if="busy" class="consent-go-spin"></span>
		<span>{{ busy ? 'Continuing…' : 'Continue' }}</span>
	</button>

	<div class="consent-foot">
		<button class="gg-link" :disabled="busy" v-on:click="$emit('switch')">Not you? Switch account</button>
		<button class="gg-link consent-cancel" :disabled="busy" v-on:click="$emit('deny')">Cancel</button>
	</div>

</div>

</template>

<script>

/*
	The "Continue as @you" screen. For a first-party (trusted) client this is a
	light confirmation, not a permission wall — but it still shows exactly what
	will be shared, so it's honest. 'openid' is implicit (you're signing in) and
	hidden from the list.
*/
export default {

	name: 'Consent',

	props: {
		client: { type: Object, required: true },
		profile: { type: Object, required: true },
		// 'connect' when an already-signed-in app is just linking the geekgroup
		// account (vs a fresh sign-in) — only the wording changes
		intent: { type: String, default: '' },
		busy: { type: Boolean, default: false },
		error: { type: String, default: '' }
	},

	data: function() {

		return { avatarFailed: false }

	},

	computed: {

		// connecting (already signed in elsewhere) reads as "connect", a fresh
		// authorization reads as "sign you in"
		sub: function() {

			return (this.intent === 'connect')
				? this.client.name + ' will connect your geekgroup account.'
				: this.client.name + ' will use your geekgroup account to sign you in.'

		},

		shown: function() {

			return (this.client.scopes || []).filter(function(s) { return s.key !== 'openid' })

		},

		initial: function() {

			return (this.profile.username || '?').charAt(0).toUpperCase()

		}

	}

}

</script>

<style scoped>


.consent-who {
	display: flex;
	align-items: center;
	gap: 13px;
	margin-top: 20px;
	padding: 13px 14px;
	background: var(--field);
	border: 1.5px solid var(--line);
	border-radius: 14px;
}

.consent-avatar {
	width: 46px;
	height: 46px;
	border-radius: 12px;
	object-fit: cover;
	flex: none;
	background: #dfe8ec;
}

.consent-avatar--fallback {
	display: flex;
	align-items: center;
	justify-content: center;
	background: var(--accent);
	color: #fff;
	font-weight: 800;
	font-size: 20px;
}

.consent-handle { font-weight: 700; font-size: 15.5px; }

.consent-name { font-size: 13px; color: var(--muted); margin-top: 1px; }

.consent-scopes { margin-top: 18px; }

.consent-scopes-head {
	font-size: 12.5px;
	font-weight: 600;
	color: var(--muted);
	margin-bottom: 9px;
}

.consent-scope {
	display: flex;
	align-items: center;
	gap: 10px;
	font-size: 13.5px;
	color: var(--ink);
	padding: 4px 0;
}

.consent-tick {
	width: 20px;
	height: 20px;
	border-radius: 50%;
	background: rgba(224, 122, 79, .18);
	color: var(--accent);
	font-size: 12px;
	font-weight: 800;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex: none;
}

.consent-go { margin-top: 22px; }

.consent-go-spin {
	width: 16px;
	height: 16px;
	border: 2px solid rgba(255, 255, 255, .4);
	border-top-color: #fff;
	border-radius: 50%;
	animation: gg-rot .7s linear infinite;
}

.consent-foot {
	display: flex;
	justify-content: space-between;
	gap: 12px;
	margin-top: 16px;
}

.consent-cancel { color: var(--muted); }

</style>
